import { useEffect, useMemo, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness } from "../api/hooks";
import { newClientId } from "../api/idempotency";
import type { Hydrant, HydrantCondition } from "../api/types";
import { can } from "../auth/roles";
import { ActionDialog } from "../components/ActionDialog";
import { StatusBadge } from "../components/Badges";
import { HYDRANT_CONDITION, PROVENANCE_SOURCE, labelOf, optionsOf, statusOf } from "../components/labels";
import { QueryState, StaleNotice } from "../components/ProblemAlert";
import { useFireContext } from "../context";
import { formatDateTime, lusakaLocalToUtc } from "../format/datetime";
import { FireMap } from "../map/FireMap";
import { Legend } from "../map/Legend";
import type { MapData } from "../map/layers";
import { lonLat, personLabel, Row, useWardNames } from "./shared";
import { Button, Chip, IconButton, IconClose } from "@lcc/ui";
import { MapPage } from "../components/MapPage";

function num(v: string | undefined, min: number, max: number, integer = false): number | null | "bad" {
  if (!v?.trim()) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < min || n > max || (integer && !Number.isInteger(n))) return "bad";
  return n;
}

function HydrantDetail({ hydrantId, onEdit, onSurvey, onClose }: { hydrantId: string; onEdit: (h: Hydrant) => void; onSurvey: (h: Hydrant) => void; onClose: () => void }) {
  const { principal } = useFireContext();
  const wardName = useWardNames();
  const q = useApi(["hydrant", hydrantId], (api) => endpoints.getHydrant(api, hydrantId));
  const h = q.data;
  const fresh = useFreshness(q);
  if (!h) return <QueryState isLoading={q.isLoading} error={q.error} label="the hydrant" />;
  return (
    <section className="fer-panel fer-record" aria-label={`Hydrant ${h.code ?? ""}`}>
      <div className="lcc-detail__head">
        <div className="lcc-detail__titles">
          <p className="lcc-eyebrow">Hydrant · {wardName(h.ward_id)}</p>
          <h3 className="lcc-detail__title">
            Hydrant {h.code ?? "(no code)"}
            {h.retired ? " (retired)" : ""}
          </h3>
          <span className="fer-badges fer-record__status">
            <StatusBadge status={statusOf(HYDRANT_CONDITION, h.condition)} />
          </span>
        </div>
        <IconButton label="Close" size="sm" icon={<IconClose size={16} />} onClick={onClose} />
      </div>
      <StaleNotice {...fresh} error={q.error} what="hydrant record" />
      <dl className="fer-dl">
        <Row term="Position">{lonLat(h.lon, h.lat)}</Row>
        <Row term="Ward">{wardName(h.ward_id)}</Row>
        <Row term="Flow">{h.flow_lpm != null ? `${h.flow_lpm} litres a minute` : "Not measured"}</Row>
        <Row term="Last surveyed">{h.last_surveyed_at ? formatDateTime(h.last_surveyed_at) : "Never"}</Row>
        <Row term="Recorded from">{h.provenance ? `${labelOf(PROVENANCE_SOURCE, h.provenance.source)}${h.provenance.reference ? ` (${h.provenance.reference})` : ""}` : "—"}</Row>
        {h.notes && <Row term="Notes">{h.notes}</Row>}
      </dl>
      <div className="fer-actions fer-actions--start">
        {!h.retired && can(principal, "surveyHydrant") && (
          <Button onClick={() => onSurvey(h)}>
            Record a survey
          </Button>
        )}
        {can(principal, "updateHydrant") && (
          <Button variant="secondary" onClick={() => onEdit(h)}>
            Correct or retire
          </Button>
        )}
      </div>
      <h4>Surveys</h4>
      {h.surveys.length === 0 ? (
        <p className="fer-muted">None.</p>
      ) : (
        <ul className="fer-plain-list" aria-label="Surveys">
          {h.surveys.map((s) => (
            <li key={s.survey_id}>
              {formatDateTime(s.captured_at)}: {statusOf(HYDRANT_CONDITION, s.condition).label}
              {s.flow_lpm != null ? `, ${s.flow_lpm} l/min` : ""}
              {s.static_pressure_bar != null ? `, ${s.static_pressure_bar} bar` : ""} ({personLabel(s.surveyed_by, principal.partyId)}
              {s.offline ? ", offline" : ""}){s.notes ? ` – ${s.notes}` : ""}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type DialogState = { kind: "register" } | { kind: "edit"; h: Hydrant } | { kind: "survey"; h: Hydrant } | null;

/** The hydrant register (every FER role reads it city-wide; it is map data), with registration, corrections and surveys. */
export function HydrantsScreen() {
  const { principal, focus, now } = useFireContext();
  const wardName = useWardNames();
  const [condition, setCondition] = useState<HydrantCondition | "">("");
  const [due, setDue] = useState(false);
  const [selected, setSelected] = useState<string | null>(focus.hydrantId ?? null);
  const [dialog, setDialog] = useState<DialogState>(null);
  useEffect(() => {
    if (focus.hydrantId) setSelected(focus.hydrantId);
  }, [focus.hydrantId]);
  const q = useApi(["hydrants", condition, due], (api) => endpoints.listHydrants(api, { condition: condition ? [condition] : undefined, surveyDue: due || undefined }), { keepPrevious: true });
  const rows = (q.data ?? []).filter((h) => !h.retired || condition);
  const data: MapData = useMemo(() => ({ hydrants: rows.map((h) => ({ id: h.hydrant_id, code: h.code ?? null, condition: h.condition, point: [h.lon, h.lat] as [number, number] })) }), [rows]);

  return (
    <MapPage
      title="Hydrants"
      head={
        <>
          <p className="fer-hint">
            {rows.length} hydrant(s) shown{q.data ? `, ${rows.filter((h) => h.condition === "working").length} working` : ""}. Every FER role reads them city-wide.
          </p>
          <div className="fer-toolbar">
            <label className="fer-field fer-field--inline">
              <span>Condition</span>
              <select value={condition} onChange={(e) => setCondition(e.target.value as HydrantCondition | "")}>
                <option value="">All</option>
                {Object.entries(HYDRANT_CONDITION).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
            <Chip selected={due} onClick={() => setDue(!due)}>
              Survey due (none in a year)
            </Chip>
          </div>
          {can(principal, "registerHydrant") && (
            <div className="fer-actions fer-actions--start">
              <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "register" })}>
                Register a hydrant
              </Button>
            </div>
          )}
        </>
      }
      map={<FireMap fill fitPadding={{ top: 60, right: selected ? 540 : 100, bottom: 80, left: 440 }} ariaLabel="Map of hydrants (the same data is in the table)" data={data} onPick={(kind, id) => kind === "hydrant" && setSelected(id)} />}
      inspector={
        selected ? (
          <HydrantDetail key={selected} hydrantId={selected} onClose={() => setSelected(null)} onEdit={(h) => setDialog({ kind: "edit", h })} onSurvey={(h) => setDialog({ kind: "survey", h })} />
        ) : null
      }
      inspectorLabel="Selected hydrant"
      legend={<Legend keys={["hydrant"]} />}
    >
      <QueryState isLoading={q.isLoading} error={q.error} label="hydrants" />
      {q.data && rows.length === 0 && <p className="fer-muted">No hydrant matches.</p>}
      {rows.length > 0 && (
        <table className="lcc-table lcc-table--compact" aria-label={`Hydrants (${rows.length})`}>
          <thead>
            <tr>
              <th scope="col">Hydrant</th>
              <th scope="col">Condition</th>
              <th scope="col">Last surveyed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.hydrant_id} className={`fer-row--click${h.hydrant_id === selected ? " fer-row--selected" : ""}`} onClick={() => setSelected(h.hydrant_id)}>
                <th scope="row">
                  <button
                    type="button"
                    className="fer-rowlink"
                    aria-label={`Open hydrant ${h.code ?? h.hydrant_id.slice(0, 8)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(h.hydrant_id);
                    }}
                  >
                    {h.code ?? "(no code)"}
                  </button>
                  <span className="fer-sub">
                    {wardName(h.ward_id)} · {lonLat(h.lon, h.lat)}
                    {h.flow_lpm != null ? ` · ${h.flow_lpm} l/min` : ""}
                  </span>
                </th>
                <td>
                  <StatusBadge status={statusOf(HYDRANT_CONDITION, h.condition)} />
                </td>
                <td>{h.last_surveyed_at ? formatDateTime(h.last_surveyed_at) : "Never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {dialog?.kind === "register" && (
        <ActionDialog
          title="Register a hydrant"
          intent="registerHydrant:new"
          intro={<p>The ward comes from the position (a fire officer registers only in their wards).</p>}
          initial={{ client_id: newClientId(), condition: "working", provenance_source: "field_survey" }}
          onDiscard={(v) => ({ ...v, client_id: newClientId() })}
          fields={[
            { name: "lon", label: "Longitude", required: true },
            { name: "lat", label: "Latitude", required: true },
            { name: "accuracy_m", label: "Position within (metres, up to 1000)", required: true },
            { name: "code", label: "Code (e.g. the water utility's number)", maxLength: 40 },
            { name: "condition", label: "Condition", kind: "select", required: true, options: optionsOf(HYDRANT_CONDITION) },
            { name: "flow_lpm", label: "Flow (litres a minute)", kind: "number", min: "0", max: "20000" },
            { name: "provenance_source", label: "Where the record comes from", kind: "select", required: true, options: optionsOf(PROVENANCE_SOURCE).filter(([k]) => k !== "owner_application") },
            { name: "provenance_reference", label: "Reference", maxLength: 200 },
            { name: "notes", label: "Notes", kind: "textarea", maxLength: 1000 },
          ]}
          submitLabel="Register"
          build={(v) => {
            const lon = num(v.lon, -180, 180);
            const lat = num(v.lat, -90, 90);
            const acc = num(v.accuracy_m, 0.001, 1000);
            const flow = num(v.flow_lpm, 0, 20000, true);
            if (lon === null || lon === "bad" || lat === null || lat === "bad") return { error: "Give longitude (-180 to 180) and latitude (-90 to 90)." };
            if (acc === null || acc === "bad") return { error: "Give how sure the position is: more than 0 and at most 1000 metres." };
            if (flow === "bad") return { error: "Flow: a whole number of litres a minute up to 20000." };
            return {
              body: {
                client_id: v.client_id,
                lon,
                lat,
                accuracy_m: acc,
                code: v.code?.trim() || null,
                condition: v.condition,
                flow_lpm: flow,
                provenance: { source: v.provenance_source, reference: v.provenance_reference?.trim() || null },
                notes: v.notes?.trim() || null,
              },
            };
          }}
          write={(api, body, key) => endpoints.registerHydrant(api, body as never, key)}
          onDone={(h) => setSelected((h as Hydrant).hydrant_id)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "survey" && (
        <ActionDialog
          title={`Survey of hydrant ${dialog.h.code ?? ""}`}
          intent={`recordHydrantSurvey:${dialog.h.hydrant_id}`}
          initial={{ client_id: newClientId(), condition: dialog.h.condition }}
          onDiscard={(v) => ({ ...v, client_id: newClientId() })}
          fields={[
            { name: "condition", label: "Condition found", kind: "select", required: true, options: optionsOf(HYDRANT_CONDITION) },
            { name: "flow_lpm", label: "Flow measured (litres a minute)", kind: "number", min: "0", max: "20000" },
            { name: "static_pressure_bar", label: "Static pressure (bar)", kind: "number", min: "0", max: "30" },
            { name: "captured_at", label: "Surveyed at (Lusaka time; empty = now)", kind: "datetime" },
            { name: "notes", label: "Notes", kind: "textarea", maxLength: 1000 },
          ]}
          submitLabel="Record the survey"
          build={(v) => {
            const flow = num(v.flow_lpm, 0, 20000, true);
            const pressure = num(v.static_pressure_bar, 0, 30);
            if (flow === "bad") return { error: "Flow: a whole number of litres a minute up to 20000." };
            if (pressure === "bad") return { error: "Pressure: 0 to 30 bar." };
            const at = v.captured_at ? lusakaLocalToUtc(v.captured_at) : now().toISOString();
            if (!at) return { error: "Give when it was surveyed as a date and time." };
            return { body: { client_id: v.client_id, captured_at: at, condition: v.condition, flow_lpm: flow, static_pressure_bar: pressure, notes: v.notes?.trim() || null } };
          }}
          write={(api, body, key) => endpoints.surveyHydrant(api, dialog.h.hydrant_id, body as never, key)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "edit" && (
        <ActionDialog
          title={`Correct hydrant ${dialog.h.code ?? ""}`}
          intro={<p>Condition and flow change only through surveys.</p>}
          initial={{ code: dialog.h.code ?? "", lon: String(dialog.h.lon), lat: String(dialog.h.lat), notes: dialog.h.notes ?? "", retired: String(dialog.h.retired) }}
          fields={[
            { name: "code", label: "Code", maxLength: 40 },
            { name: "lon", label: "Longitude", required: true },
            { name: "lat", label: "Latitude", required: true },
            { name: "notes", label: "Notes", kind: "textarea", maxLength: 1000 },
            { name: "retired", label: "Retired (removed from the maps)", kind: "checkbox" },
          ]}
          submitLabel="Save"
          build={(v) => {
            const h = dialog.h;
            const body: Record<string, unknown> = {};
            if ((v.code?.trim() || null) !== (h.code ?? null)) body.code = v.code?.trim() || null;
            const lon = num(v.lon, -180, 180);
            const lat = num(v.lat, -90, 90);
            if (lon === null || lon === "bad" || lat === null || lat === "bad") return { error: "Give longitude (-180 to 180) and latitude (-90 to 90)." };
            if (lon !== h.lon || lat !== h.lat) Object.assign(body, { lon, lat });
            if ((v.notes?.trim() || null) !== (h.notes ?? null)) body.notes = v.notes?.trim() || null;
            if ((v.retired === "true") !== h.retired) body.retired = v.retired === "true";
            return Object.keys(body).length ? { body } : { error: "Nothing has changed." };
          }}
          write={(api, body) => endpoints.updateHydrant(api, dialog.h.hydrant_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
    </MapPage>
  );
}
